import { AppContext } from "App";

import { createDeleteFormAction, createInitFormAction } from "@multiweb/forms";
import { bridge } from "@multiweb/reactCore/bridge";
import { StoreManager } from "@multiweb/reactSetup";
import { DataConverter } from "Global/Base/Converters/DataConverter";
import { GenericControl } from "UI/UI.BaseControls/GenericControl";
import { IFormRight } from "UI/UI/Rights/FormRight";
import { getExtendedPropertiesFromElement, getTagsFromElement } from "UI/utils";
import { AwaitableStandardEvent, Event } from "../../../../Global/Base/Events";
import { NotImplementedException } from "../../../../Global/Base/Exceptions";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IRightConditionItem } from "../../../../OW.Remoting/Remoting/Rights";
import { FormControlParams, IHTMLElementWithControl } from "../Controls/ControlInterfaces";
import { IFRMParams } from "../Controls/FormParameters";
import { SidePanel } from "../Controls/SidePanel";
import { IFormUI } from "../FormUI/FormUI";
import { UIManager } from "../UIManager";
import { FormContext, IWindowable, WindowState } from "../UIManagerInterfaces";
import { OpenModeType } from "./Actions/DetailFormAction";
import { FilterPanel } from "./Filters/FilterPanel";
import { FormControl } from "./FormControl";
import { FormProfileManager } from "./Forms/FormProfileManager";
import { FormResult } from "./Forms/FormResult";
import { IFormProfileInfo } from "./Forms/IFormProfileInfo";
import { IFormProfileManager } from "./Forms/IFormProfileManager";
import { IFrameWithProfile, IMasterableFrame } from "./Frames/FrameInterfaces";
import { IDetailFrame } from "./Frames/IDetailMasterableFrame";
import { IForm, IFormClosingCancelledEventArgs, IFormSavingParameters } from "./IForm";
import { IFrame } from "./IFrame";

const { dispatchAction } = bridge;

/**
 * Parametry konstruktoru tridy {@link BaseForm}.
 */
export type BaseFormParams = FormControlParams & {
    formInstanceId: string;
    registerToBrowserHistory?: boolean;
};

export abstract class BaseForm extends FormControl implements IForm {
    //#region Fields
    public readonly formName: string;
    public readonly formInstanceId: string;
    public readonly profileId: string;
    public readonly height: number;
    public readonly width: number;
    public readonly isPreviewMode: boolean;
    public readonly lovFrame: string | undefined;

    public readonly formProfileChanged: Event<IForm, void> = new Event<IForm, void>();
    public readonly framesInitialized: Event<IForm, void> = new Event<IForm, void>();

    /** Vrací kontext formuláře spolu s rolemi, které měly právo formulář zobrazit v menu */
    private _formContext: FormContext;
    public openMode: OpenModeType = OpenModeType.standard;
    public masterObjectChanged: Event<IForm, OWObject> = new Event<IForm, OWObject>();
    public onDoOpen: Event<IWindowable, void> = new Event<IWindowable, void>();
    //#region IWindowableEvents
    public stateChanged: Event<IWindowable, { state: WindowState; value: any }> = new Event<IWindowable, { state: WindowState; value: any }>();
    public closed: Event<IWindowable, FormResult> = new Event<IWindowable, FormResult>();
    public closing: Event<IWindowable, boolean> = new Event<IWindowable, boolean>();
    public closingPreview: AwaitableStandardEvent<IWindowable, void> = new AwaitableStandardEvent<IWindowable, void>();
    public closingCancelled: Event<IWindowable, IFormClosingCancelledEventArgs> = new Event<IWindowable, IFormClosingCancelledEventArgs>();
    //#endregion IWindowableEvents
    public childOpened: Event<IWindowable, void> = new Event<IWindowable, void>(); // Událost vyvolaná pokud se otevře child tohoto formuláře
    public childClosed: Event<IWindowable, void> = new Event<IWindowable, void>();
    public readOnlyChanged: Event<IForm, boolean> = new Event<IForm, boolean>();
    /** Je vyvolána v případě, kdy došlo ke změně titulku formuláře */
    public titleChanged: Event<IForm, string> = new Event<IForm, string>();

    protected _isDetailForm: boolean = false;
    public get isDetailForm(): boolean {
        return this._isDetailForm;
    }

    public setAttributesOnNewObject: string;
    public showDetail: boolean = true;

    public hideWindowActions: boolean;
    public visibleOnlyDetail: boolean = false;
    public filterPanel: FilterPanel = new FilterPanel();
    public formSettingsPanel: SidePanel = new SidePanel();

    public viewMode: boolean = undefined;

    private _formUI: IFormUI;
    public get formUI(): IFormUI {
        return this._formUI;
    }
    public set formUI(value: IFormUI) {
        this._formUI = value;
    }

    public canClose: boolean = false;
    public subtitle: string = "";

    /**
     * Seznam podmínek z definice formuláře
     */
    public conditionItems: IRightConditionItem[] = new Array<IRightConditionItem>();
    public usedConditionItemsId: Array<string> = new Array<string>();

    protected readonly _frames: FramesArray;
    protected readonly _profileManager: IFormProfileManager;
    protected readonly _masterableFrames: IMasterableFrame[];
    protected readonly _topLevelFrames: Array<IFrame>;

    protected _id: number | undefined;

    protected _setValues: string;
    protected _registerToBrowserHistory: boolean;
    protected _readVersioned: boolean = false;

    /** Režim formuláře, ve kterém nejsou při jeho otevření načteny žádná data */
    protected _queryMode: { formValue: boolean | null; profileValue: boolean | null } = { formValue: null, profileValue: null };
    protected _readOnly: boolean = false;
    protected _openInEdit: boolean = false;

    protected _canRead: boolean = false;
    protected _canNew: boolean = false;
    protected _canDelete: boolean = false;
    protected _canInsertForm: boolean = false;
    protected _detailFrame: IDetailFrame;
    protected _isActive: boolean = false;
    protected _formRights: Array<IFormRight> = new Array<IFormRight>();
    protected _isImplicit = false;
    protected _formVisible = false;
    protected _realVisibility = false;
    protected _multiEditMode = false;
    public get isFormVisible(): boolean {
        return this._formVisible && this._realVisibility;
    }
    //#endregion Fields

    //#region Properties
    public get canInsertAtFormLevel(): boolean {
        return this._canInsertForm;
    }

    /** Nastaví kontext formuláře */
    public set formContext(ctx: FormContext) {
        this._formContext = ctx;
    }

    /**
     * Seznam rolí, které umožnili načtení položky menu (nepovinný)
     */
    public get formContext(): FormContext {
        return this._formContext;
    }

    protected _transactionContextGetter?: () => string | undefined;

    /**
     * @inheritdoc
     */
    public get transactionContextGetter(): () => string | undefined {
        return this._transactionContextGetter;
    }

    /**
     * Jednoznačný číselný identifikátor formuláře
     */
    public get id(): number | undefined {
        return this._id;
    }

    public get readOnly(): boolean {
        return this._readOnly;
    }

    /**
     * Formulář se dostavá do stavu read only a všechny framy na něm musí reagovat a přepnout se také
     */
    public set readOnly(readOnly: boolean) {
        if (this._readOnly !== readOnly) {
            this._readOnly = readOnly;
            this.readOnlyChanged.trigger(this, readOnly);
        }
    }

    public get setValues(): string {
        return this._setValues;
    }

    public set setValues(value: string) {
        this._setValues = value;
    }

    /**
     * Vrací true, zda se na (modálním) formuláři mají načítat verzované záznamy
     */
    public get readVersioned(): boolean {
        return this._readVersioned;
    }

    public get masterableFrames(): Array<IMasterableFrame> {
        return this._masterableFrames;
    }

    public get detailFrame(): IDetailFrame {
        return this._detailFrame;
    }

    /**
     * Výsledek tvrdého práva čtení na formulářové úrovni (podmínka se uplatňuje do podmínky čtení hlavního seznamu)
     */
    public get canRead(): boolean {
        return this._canRead;
    }

    /**
     * Výsledek tvrdého práva zakládání záznamů na formulářové úrovni
     */
    public get canNew(): boolean {
        return this._canNew;
    }

    /**
     * Výsledek tvrdého práva mazání na formulářové úrovni
     */
    public get canDelete(): boolean {
        return this._canDelete;
    }

    public get multiEditMode(): boolean {
        return this._multiEditMode;
    }

    /**
     * Kolekce všech framů
     */
    public get frames(): Array<IFrame> {
        return this._frames;
    }

    public get isEdited(): boolean {
        throw new NotImplementedException();
    }

    public get openInEdit(): boolean {
        throw new NotImplementedException();
    }

    /**
     * Práva definována na úrovni formuláře
     */
    public get formRights(): Array<IFormRight> {
        throw new NotImplementedException();
    }

    /**
     * Vrací id profilu, který se má po otevření formuláře aplikovat
     */
    public get initialFormProfileId(): number {
        throw new NotImplementedException();
    }

    /**
     * Nastavení profilu pro formulář
     */
    public get profileInfo(): IFormProfileInfo {
        throw new NotImplementedException();
    }

    /**
     * Get pro element - pokud by se element nastavil na private, aby se přišlo na to, kde k tomu lidi přistupují špatně,
     * tzn. z jiných controlů a tříd na grafickou prezentaci nějakého controlu
     */
    public getElement(): HTMLElement {
        return this.element;
    }

    protected _returnMainInstance?: boolean;
    public get returnMainInstance(): boolean | undefined {
        return this._returnMainInstance;
    }

    /**
     * @inheritDoc
     */
    public get isImplicit(): boolean {
        return this._isImplicit;
    }
    //#endregion Properties

    //#region Constructors
    /**
     * Pro inicializaci je potřeba navíc volat metodu init, implementovanou na konkrétním childu
     * @param onwerForm
     * @param element
     * @param formInstanceId Identifikátor otevřené instance formuláře.
     * @param registerToBrowserHistory
     */
    constructor({ formInstanceId, registerToBrowserHistory = true, ...otherParams }: BaseFormParams) {
        super(otherParams);

        this.formInstanceId = formInstanceId;
        this._registerToBrowserHistory = registerToBrowserHistory;

        // Kolekce všech nanalezených framů
        this._frames = new FramesArray();
        this._frames.initialize(this);
        this._masterableFrames = [];
        this._topLevelFrames = new Array<IFrame>();
        this._profileManager = new FormProfileManager(this, AppContext.instance.currentUser);

        this.height = parseFloat(this.element.getAttribute("data-height"));
        this.width = parseFloat(this.element.getAttribute("data-width"));
        this.formName = this.element.getAttribute("data-name");
        this.isPreviewMode = DataConverter.toBoolean(this.element.getAttribute("data-xgenPreviewMode"));
        this.lovFrame = this.element.getAttribute("data-lovframe");

        this.initReduxState();
    }
    //#endregion Constructors

    //#region Methods
    public get profileManager(): IFormProfileManager {
        return this._profileManager;
    }

    /** Nastaví resize listener na Formu */
    public static resizeEventMethod(): void {
        UIManager.instance.windowResized.trigger(null, null);
    }

    /**
     * Propsané metody kvůli korektní implementaci rozhraní IForm
     * potomci si je přepíšou, když je budou potřebovat
     */
    public abstract setVisibleFrame(frame: IFrame, addToUIHistory?: boolean): void;
    public abstract setVisibleFrame(frame: string, addToUIHistory?: boolean): void;
    public abstract setVisibleFrame(frame: any, addToUIHistory?: boolean): void;

    public abstract checkAndSaveForm(param: IFormSavingParameters): Promise<boolean>;

    public abstract actualizeForm(): Promise<void>;

    public abstract waitForBussyFrameAndChildren(sourceFrame: IFrame): Promise<void>;

    public abstract setToolbarToEditMode(inEdit: boolean): Promise<void>;

    public abstract close(result?: FormResult): Promise<boolean>;

    public abstract setState(state: WindowState, value: any): void;

    public abstract getState(state: WindowState): boolean;

    public async init(_formUI: IFormUI): Promise<void> {
        // prázdné tělo, inicializace je volitelná
    }

    public initReduxState(): void {
        StoreManager.initializeStore(!!AppContext.instance.configuration.developmentMode);
        dispatchAction(
            createInitFormAction(
                this.element.id,
                getExtendedPropertiesFromElement(this.element),
                getTagsFromElement(this.element),
            ),
        );
    }

    /**
     * Nastaví Form podle vstupních dat
     * @param paramData Vstupní data
     */
    public abstract setParametricData(paramData: IFRMParams): void;

    /**
     * Zruší aktivní profil a nastaví formulář do výchozího stavu
     */
    public abstract clearProfileInfo(): void;

    /**
     * Přidá side panel
     * @param panel hmtlElement přidávaného panelu
     */
    public abstract addSidePanel(panel: HTMLElement): void;

    public addDisposable(object: IDisposable): void {
        this._disposableObjects.add(object);
    }
    //#endregion Methods

    //#region IDisposable
    public override dispose(): void {
        if (this.element) {
            const frames = Array.from<IHTMLElementWithControl>(this.element.querySelectorAll("[data-behaviour='frame']"));
            const widgets = Array.from<IHTMLElementWithControl>(this.element.querySelectorAll("[data-behaviour='form']"));

            while (frames.length > 0) {
                const frameElement = frames.pop(); // Dispose from end to dispose nested frames first, otherwise it won't find parent.
                const parentForm = GenericControl.findParent(frameElement, parent => parent.matches("[data-behaviour='form']"));
                // Pokud je nejbližší parent Form jiný než aktuální, nechám volání dispose na něm.
                if (parentForm !== this.element) {
                    continue;
                }

                const frame = frameElement.tscControl;
                if (frame instanceof IDisposable)
                    frame.dispose();

                frameElement.tscControl = null;
            }

            if (widgets) {
                for (const widget of widgets) {
                    if (widget.tscControl instanceof IDisposable) {
                        widget.tscControl.dispose();
                    }
                }
            }
        }

        this.formUI?.dispose();

        this.formRights.length = 0;
        this._masterableFrames.length = 0;
        this._topLevelFrames.length = 0;

        dispatchAction(createDeleteFormAction(this.element.id));
        this.filterPanel?.dispose();
        this._profileManager?.dispose();

        super.dispose();
    }
    //#endregion IDisposable
}

export class FramesArray extends Array<IFrame> {
    // Overwrite species to the parent Array constructor
    public static override get [Symbol.species](): ArrayConstructor {
        return Array;
    }

    private _form: BaseForm;

    public initialize(form: BaseForm): void {
        this._form = form;
    }

    public override push(...params: IFrame[]): number {
        for (const frame of params) {
            if (frame instanceof IFrameWithProfile) {
                // Některá z vlastností aktuálního profilu byla změněna
                frame.frameProfileChanged.add(() => {
                    this._form.formProfileChanged.trigger(this._form, null);
                });
            }
        }

        return super.push(...params);
    }
}
